import { zodResolver } from '@hookform/resolvers/zod';
import {
  DeliveriesController_record_v1,
  type DeliveriesController_record_v1Body,
  type DeliveryResource,
  type OutletResource,
  OutletsController_list_v1,
  type OutletStockResource,
  type StockLevelResource,
  StockLevelsController_read_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCircleCheck,
  IconPlus,
  IconTrash,
  IconWifiOff,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isOffline, messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatInstant } from '@/lib/instant';
import { INSTANT_MALFORMED, localInstantNow } from '@/lib/local-instant';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { EmptyState, LoadFailed, Screen, ScreenFact } from '../back-office/Screen';
import { labelForKind } from '../outlets/outlet-kinds';
import { paths } from '../paths';
import {
  blankLine,
  deliveryContentFrom,
  deliveryNoteSchema,
  type DeliveryNoteValues,
  fingerprintOf,
  MAX_DELIVERY_LINES,
  unitsOn,
} from './delivery-note';
import { formatLevel } from './stock-levels';

/**
 * Goods arrived: `POST /v1/outlets/{outletId}/stock/deliveries`.
 *
 * A delivery note, entered from the paper one — an outlet, when the consignment
 * turned up, and a line per item on it. One of the three acts that change a
 * level (CL-502), and the only screen in this app that adds to one.
 *
 * `delivery-note.ts` owns what a note is and what it may say;
 * `lib/write-attempt.ts` owns the idempotency key and the body's id, which are
 * the subtle part of this ticket and are argued there rather than here. That
 * module is shared with the correction form (FE038) and is where the stocktake
 * form should take its own rule from.
 *
 * ## Manager or above, unlike the levels screen next door
 *
 * `OutletStock` is `requireSignedIn()`, mirroring `@RequireRole()` on the read.
 * This is a **write**: `DeliveriesController` declares
 * `@RequireRole('OWNER', 'MANAGER')` and `@RequireOutletScope()`, and the route
 * entry mirrors it exactly. There is no till screen for any of this — a terminal
 * moves stock as a side effect of selling, and a cashier never records a
 * delivery — so nothing here is duplicated in the till shell.
 *
 * ## The picker is the duplicate rule
 *
 * The same item on two lines is refused outright and never collapsed
 * (`STOCK_DUPLICATE_ITEM_IN_REQUEST`, FR-534), so an item already on the note is
 * taken out of every other line's options: the refusal is prevented rather than
 * explained afterwards. The schema states it as well, on the array, for the case
 * where the options are stale — both halves are described in `delivery-note.ts`.
 *
 * ## Where the items come from, and why it is the levels read
 *
 * The options are `GET /v1/outlets/{outletId}/stock` — the same read
 * `OutletStock` renders. That route answers exactly the items the outlet stocks
 * **and** that track stock (`ci.tracks_stock` is a conjunct of its query), which
 * is precisely the set a delivery line may name: anything outside it is refused
 * `STOCK_ITEM_NOT_TRACKED`. Drawing the picker from the outlet's catalogue
 * instead would offer ticket types and untracked items and earn that refusal for
 * an ordinary choice.
 *
 * It also carries the level, which is worth having in front of somebody entering
 * a note: a level that reads far from what the shelf looks like is usually the
 * wrong outlet. Levels are shown **as they stand** — zero is a row, and a
 * negative is real and is not clamped or hidden (CL-501, FR-510). `formatLevel`
 * is the same one the levels table uses.
 *
 * ## Outlets that carry no stock are not offered
 *
 * `STOCK_OUTLET_CARRIES_NO_STOCK` is a fact about a place rather than about
 * anything on the form, so the picker lists only outlets that hold goods —
 * `docs/ui-conventions.md` asks that a control the caller cannot use be hidden
 * rather than left to refuse. The refusal is still handled: an outlet whose flag
 * changed after this screen loaded reaches it, and `messageForFailure` words it
 * from the code.
 */

/**
 * What an item reads as, in the picker and on the trigger once it is chosen.
 *
 * The code first, because that is what a delivery note is written in and what
 * somebody is reading off the paper in front of them.
 */
const itemLabel = (item: StockLevelResource): string => `${item.code} — ${item.name}`;

/** Everything the item picker can be in. One value, so two cannot coexist. */
type ItemsState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly stock: OutletStockResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** A note that was recorded, kept to say so afterwards. */
interface Recorded {
  readonly delivery: DeliveryResource;
  readonly outletName: string;
}

export function RecordDelivery() {
  const client = useApiClient();

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  const [items, setItems] = useState<ItemsState>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  /**
   * The key and the id this note is currently being attempted under.
   *
   * A ref rather than state: nothing renders from it, and it has to be read and
   * written inside one submit without waiting for a render to see the new value.
   * `lib/write-attempt.ts` owns the rule — one attempt per payload, so an
   * unchanged note replays after a timeout and an edited one gets a fresh key —
   * and it mints the body's `id` alongside, which is what keeps a retry
   * byte-identical.
   */
  const attempt = useRef<WriteAttempt | null>(null);

  const [failure, setFailure] = useState<unknown>(null);
  const [recorded, setRecorded] = useState<Recorded | null>(null);

  const form = useForm({
    resolver: zodResolver(deliveryNoteSchema),
    mode: 'onTouched',
    defaultValues: {
      outletId: '',

      // Today, now, because a delivery is nearly always entered as it is
      // unloaded — and changeable, because the other case is a note found on a
      // desk on Thursday for goods that came on Monday. The API stores this and
      // orders nothing by it (FR-009), so a wrong answer misleads a person
      // reading the movement log rather than the system.
      arrivedAt: localInstantNow(),
      lines: [blankLine()],
    },
  });

  const lines = useFieldArray({ control: form.control, name: 'lines' });

  const outletId = form.watch('outletId');
  const lineValues = form.watch('lines');

  const pending = form.formState.isSubmitting;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park itself, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired outlets are
    // excluded by the API's own default (FR-112) — a retired outlet takes no
    // deliveries.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);
      })
      .catch(() => {
        // The picker is the only casualty and the screen says so where the
        // picker is. `outletsAnswered` stays false: a failed read has answered
        // nothing about which outlets hold goods, and the notice must not tell
        // somebody the park has none because a request timed out.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    if (outletId === '') {
      setItems({ kind: 'idle' });

      return;
    }

    // Two guards. `controller` stops the request when the outlet changes, and
    // `live` stops an answer already in flight from landing after this effect
    // was torn down — without it, switching outlets twice quickly can leave the
    // second outlet's items in the first outlet's picker.
    const controller = new AbortController();
    let live = true;

    setItems({ kind: 'loading' });

    client
      .request(StockLevelsController_read_v1, {
        path: { outletId },

        // No `asOf`. The levels here are context for somebody entering a note,
        // and the only useful moment for that is now — which is the server's
        // now, not this device's (R-508).
        query: {},
        signal: controller.signal,
      })
      .then((stock) => {
        if (live) setItems({ kind: 'loaded', stock });
      })
      .catch((error: unknown) => {
        if (live) setItems({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, reloads]);

  /**
   * The outlets a delivery can be aimed at.
   *
   * An outlet that carries no stock cannot take one at all — the refusal is
   * about the place rather than about the form — so it is not offered.
   */
  const deliverableOutlets = useMemo(
    () => outlets.filter((outlet) => outlet.carriesStock),
    [outlets],
  );

  // Base UI resolves what the trigger shows from `items` rather than from the
  // rendered options, so a picker without it shows a bare UUID where a name
  // belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => deliverableOutlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [deliverableOutlets],
  );

  const outlet = deliverableOutlets.find((candidate) => candidate.id === outletId);

  // Memoised rather than a bare ternary: it is a dependency of the map below,
  // and a fresh `[]` on every render would rebuild that map on every render too.
  const stocked = useMemo<readonly StockLevelResource[]>(
    () => (items.kind === 'loaded' ? items.stock.items : []),
    [items],
  );

  // What the trigger reads a chosen item back as, for every item — not only the
  // ones a given line may still choose, or a line's own choice would render as
  // a UUID the moment it was taken out of the other lines' options.
  const stockedItems = useMemo(
    () => stocked.map((item) => ({ label: itemLabel(item), value: item.catalogueItemId })),
    [stocked],
  );

  /** The items already spoken for, which is how the duplicate refusal is prevented. */
  const chosen = useMemo(
    () =>
      new Set(
        lineValues
          .map((line) => line.catalogueItemId)
          .filter((id) => id !== '')
          .map((id) => id.toLowerCase()),
      ),
    [lineValues],
  );

  /** What one line may choose: anything not on another line, plus its own choice. */
  const optionsFor = (index: number): readonly StockLevelResource[] => {
    const mine = lineValues[index]?.catalogueItemId.toLowerCase() ?? '';

    return stocked.filter(
      (item) =>
        !chosen.has(item.catalogueItemId.toLowerCase()) ||
        item.catalogueItemId.toLowerCase() === mine,
    );
  };

  const levelFor = (catalogueItemId: string): StockLevelResource | undefined =>
    stocked.find((item) => item.catalogueItemId === catalogueItemId);

  const canAddLine =
    items.kind === 'loaded' &&
    lines.fields.length < Math.min(MAX_DELIVERY_LINES, stocked.length) &&
    // Nothing to put on a new line until the one before it has an item, and an
    // empty row nobody can fill is a control that cannot do anything.
    chosen.size >= lines.fields.length;

  const record = async (values: DeliveryNoteValues) => {
    // Belt and braces with the disabled button. A second submit carries the same
    // key and the same payload so the API would replay rather than write twice,
    // but it would still put this form into a second in-flight state for nothing.
    if (pending) return;

    setFailure(null);
    setRecorded(null);

    // The shape comes from the generated client; no path, body or response type
    // is written down on this screen.
    const content = deliveryContentFrom(values);

    if (content === null) {
      // Unreachable past the resolver, kept as a refusal on the field rather
      // than an assertion: a change that let a malformed moment through would
      // show as a field that refuses rather than as a request carrying
      // `undefined`.
      form.setError('arrivedAt', { message: INSTANT_MALFORMED });

      return;
    }

    // One attempt per payload. Unchanged note, same key and same id, so a
    // network failure or a timeout replays instead of counting the goods twice;
    // corrected note, fresh key, so a refusal that was fixed is not quarantined
    // as `SYNC_IDEMPOTENCY_KEY_REUSED`. `lib/write-attempt.ts` is the argument.
    const sending = attemptFor(attempt.current, fingerprintOf(values.outletId, content));

    attempt.current = sending;

    try {
      const body: DeliveriesController_record_v1Body = { id: sending.id, ...content };

      const delivery = await client.request(DeliveriesController_record_v1, {
        path: { outletId: values.outletId },
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
      });

      setRecorded({ delivery, outletName: outlet?.name ?? 'the outlet' });

      // The delivery is written, so the next one is a different act however
      // similar it looks — two identical notes for one outlet on one day are two
      // real consignments and the second must not replay the first one's answer.
      attempt.current = null;

      // The outlet stays: several notes for one outlet in a sitting is the
      // ordinary case. The moment goes back to now rather than keeping the last
      // one, which would silently backdate the second consignment to the first
      // one's arrival.
      form.reset({ outletId: values.outletId, arrivedAt: localInstantNow(), lines: [blankLine()] });

      // The levels behind the picker have just moved. Re-read them, so the note
      // after this one is entered against what the shelf now says.
      reload();
    } catch (error: unknown) {
      // Nothing is cleared. Every refusal this route answers with is corrected
      // by editing the note, and a form that emptied itself would take the
      // corrected version with it.
      setFailure(error);
    }
  };

  return (
    <Screen
      title="Record a delivery"
      // Only when there is something standing to say. Every fact on this bar is
      // conditional, and `Screen` draws the band for a fragment that renders
      // nothing — which on a note nobody has started is an empty ruled strip
      // under the head.
      aside={
        outlet === undefined && items.kind !== 'loaded' ? undefined : (
          <>
            {outlet !== undefined && (
              <ScreenFact label="Delivering to">
                <span className="font-mono numeric">{outlet.code}</span> — {outlet.name}
                <br />
                <span className="text-muted-foreground">{labelForKind(outlet.kind)}</span>
              </ScreenFact>
            )}

            {items.kind === 'loaded' && (
              <ScreenFact label="On this note">
                <span className="font-mono numeric">{lines.fields.length}</span>{' '}
                {lines.fields.length === 1 ? 'line' : 'lines'},{' '}
                <span className="font-mono numeric">{unitsOn(lineValues)}</span>{' '}
                {unitsOn(lineValues) === 1 ? 'item' : 'items'}
              </ScreenFact>
            )}

            {items.kind === 'loaded' && (
              // The instant the server answered the levels for, echoed from the
              // response rather than from this device's clock — it is what the
              // figures beside each item are as at.
              <ScreenFact label="Levels as at">
                <span className="font-mono numeric">{formatInstant(items.stock.asOf)}</span>
              </ScreenFact>
            )}
          </>
        )
      }
      // The related screens, as on the other stock forms. Passing `guidance`
      // is also what sets this as a form rather than a list.
      guidance={
        <div className="space-y-2 text-sm">
          <Link
            to={paths.stock}
            className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            What this outlet holds
            <IconArrowRight className="size-4" aria-hidden />
          </Link>
          <br />
          <Link
            to={paths.stockMovements}
            className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            Every movement, newest first
            <IconArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
      }
    >
      {recorded !== null && (
        // `status` and not the component's default `alert`: a delivery that was
        // recorded is not an interruption. Above the form and it stays there —
        // a toast vanishes, and somebody who looked away has no way back to it.
        <Alert role="status">
          <IconCircleCheck />
          <AlertTitle>Delivery recorded</AlertTitle>
          <AlertDescription>
            <p>
              <span className="font-mono numeric">{recorded.delivery.lines.length}</span>{' '}
              {recorded.delivery.lines.length === 1 ? 'line' : 'lines'} and{' '}
              <span className="font-mono numeric">
                {recorded.delivery.lines.reduce((total, line) => total + line.quantity, 0)}
              </span>{' '}
              items onto {recorded.outletName}&rsquo;s shelves.
            </p>
            <Button
              variant="outline"
              className="mt-3"
              // `nativeButton={false}` because the rendered element is an
              // anchor. Base UI otherwise warns that native button semantics
              // have been removed, and it is right to: a link that claims to be
              // a button is announced as one and then does not answer the space
              // bar. The two `Button render={<Link />}` sites already in the app
              // omit it and print that warning; they are other tickets' files.
              nativeButton={false}
              render={<Link to={paths.stock} />}
            >
              See what the outlet holds now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        {/* One column, per docs/ui-conventions.md. `noValidate` because the
            browser's own bubbles are not this app's error language and cannot be
            worded. */}
        <form
          className="space-y-6"
          noValidate
          onSubmit={(event) => void form.handleSubmit(record)(event)}
        >
          <div className="space-y-5">
            <FormField
              control={form.control}
              name="outletId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Outlet
                  </FormLabel>
                  <Select
                    // `''` is how the form says "nothing chosen"; Base UI says
                    // it with `null`, and handing it an empty string would have
                    // the trigger look up a label for one.
                    value={field.value === '' ? null : field.value}
                    items={outletItems}
                    onValueChange={(next) => {
                      const chosenOutlet = typeof next === 'string' ? next : '';

                      if (chosenOutlet === field.value) return;

                      field.onChange(chosenOutlet);

                      // The lines name items that belong to the outlet they were
                      // picked at. Carried across to another outlet they are
                      // either unavailable there — `STOCK_ITEM_NOT_TRACKED` — or,
                      // worse, valid, and the note lands on the wrong shelf.
                      lines.replace([blankLine()]);
                    }}
                    disabled={outletItems.length === 0 || pending}
                  >
                    <FormControl
                      render={
                        <SelectTrigger
                          aria-label="Outlet"
                          className={`${fieldSurface} h-11 w-full`}
                        >
                          <SelectValue placeholder="Choose an outlet" />
                        </SelectTrigger>
                      }
                    />
                    <SelectContent>
                      {/* The group carries the popup's padding. Items placed
                          straight into the content sit flush against its edges. */}
                      <SelectGroup>
                        {deliverableOutlets.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {outletsAnswered && deliverableOutlets.length === 0 && (
                    <FormDescription>No outlet in reach carries stock.</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="arrivedAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Arrived
                  </FormLabel>
                  <FormControl
                    render={
                      <Input
                        // The same native control the two look-up-a-moment
                        // screens use, and typed in the zone the movement log
                        // renders in — so a time can be read off a note and
                        // typed straight back without converting to UTC in
                        // anybody's head.
                        type="datetime-local"
                        className={`${fieldSurface} h-11 w-full numeric`}
                        required
                        {...field}
                        // After the spread, deliberately: react-hook-form's
                        // `field` carries a `disabled` of its own and spreading
                        // it last would put this back to `undefined`.
                        disabled={pending}
                      />
                    }
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <section className="space-y-4" aria-labelledby="delivery-lines">
            <h2
              id="delivery-lines"
              className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase"
            >
              The delivery note
            </h2>

            {items.kind === 'idle' && (
              <Notice>
                <EmptyState title="Choose an outlet first" description="Its items appear here." />
              </Notice>
            )}

            {items.kind === 'loading' && (
              // A skeleton the shape of a line, not a spinner: the block below
              // is what lands here, so the page does not jump when it does.
              <div aria-hidden className="space-y-3 rounded-2xl border border-border p-5">
                <div className="h-4 w-1/3 animate-pulse rounded-3xl bg-muted" />
                <div className="h-11 w-full animate-pulse rounded-3xl bg-muted" />
                <div className="h-11 w-2/5 animate-pulse rounded-3xl bg-muted" />
              </div>
            )}

            {items.kind === 'failed' && (
              <Notice>
                <LoadFailed error={items.error} onRetry={reload} />
              </Notice>
            )}

            {items.kind === 'loaded' && stocked.length === 0 && (
              <Notice>
                <EmptyState
                  title="This outlet stocks nothing yet"
                  description="No item that counts stock is sold here yet."
                />
              </Notice>
            )}

            {items.kind === 'loaded' && stocked.length > 0 && (
              <>
                <ul className="space-y-4">
                  {lines.fields.map((line, index) => (
                    <li key={line.id} className="rounded-2xl border border-border p-5">
                      <LineFields
                        control={form.control}
                        index={index}
                        options={optionsFor(index)}
                        allItems={stockedItems}
                        level={levelFor(lineValues[index]?.catalogueItemId ?? '')}
                        disabled={pending}
                        onRemove={
                          // The API takes at least one line and so does the
                          // schema, so the last one has nothing to remove to.
                          lines.fields.length > 1 ? () => lines.remove(index) : null
                        }
                      />
                    </li>
                  ))}
                </ul>

                <Button
                  type="button"
                  variant="outline"
                  disabled={!canAddLine || pending}
                  onClick={() => lines.append(blankLine())}
                >
                  <IconPlus data-icon="inline-start" />
                  Add another item
                </Button>
              </>
            )}
          </section>

          {failure !== null && (
            // Above the submit and it stays there — a toast vanishes, and
            // somebody who looked away has no way to get it back. The message is
            // worded from the API's `code` by `messageForFailure`; this screen
            // writes no refusal copy of its own.
            //
            // No retry button beside it, on purpose. Every refusal this route
            // answers with is the note's own to correct and the identical
            // request fails identically, so the only button that could help is
            // the one below — which, on the network case, carries the same
            // payload and therefore the same key, and is replayed rather than
            // written twice.
            //
            // The network is not dressed as a refusal: `docs/ui-conventions.md`
            // is explicit that being unable to reach the server is an operating
            // mode rather than a fault, and the destructive variant here would
            // read as the delivery having been rejected.
            <Alert variant={isOffline(failure) ? 'default' : 'destructive'}>
              {isOffline(failure) ? <IconWifiOff /> : <IconAlertTriangle />}
              <AlertDescription>{messageForFailure(failure)}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-4">
            {/* The screen's one accent. */}
            <Button
              type="submit"
              disabled={pending || items.kind !== 'loaded' || stocked.length === 0}
            >
              {pending ? 'Recording…' : 'Record delivery'}
            </Button>

            <p className="text-sm text-muted-foreground">Cannot be edited afterwards.</p>
          </div>
        </form>
      </Form>
    </Screen>
  );
}

/**
 * Where the lines would be, when there are none to draw.
 *
 * The card `ScreenTable` gives a list, without the table — this section is a
 * form rather than rows, so `TableNotice` has nothing to span. Same surface,
 * same centred measure, so choosing an outlet does not change the shape of the
 * page.
 */
function Notice({ children }: { children: ReactNode }) {
  return (
    // **No fill.** The panel is the surface, so a `bg-card` box here is a card
    // inside a card; and `bg-muted` under `EmptyState` would put the muted
    // description on the muted ground, which is the pairing that misses AA. What
    // is left is the outline of the thing it stands in for — the same
    // `rounded-2xl border border-border` a delivery line takes — so choosing an
    // outlet does not change the shape of the page.
    <div className="rounded-2xl border border-border px-6 py-12">
      <div className="mx-auto max-w-md space-y-3 text-center">{children}</div>
    </div>
  );
}

/**
 * One line of the note: what arrived, and how many.
 *
 * Two fields side by side above `sm`, which is not the second column
 * `docs/ui-conventions.md` forbids. That rule is about a *form* laid out in two
 * columns — it fails at a till and on a narrow window — and this is one repeated
 * record with two facts on it, the way the paper note it is copied from reads.
 * It collapses to one column below `sm`, and the form around it is one column
 * throughout.
 */
function LineFields({
  control,
  index,
  options,
  allItems,
  level,
  disabled,
  onRemove,
}: {
  control: ReturnType<typeof useForm<DeliveryNoteValues>>['control'];
  index: number;

  /** What this line may still choose: everything not already on another line. */
  options: readonly StockLevelResource[];

  /** Every stocked item, which is what the trigger reads a chosen one back from. */
  allItems: readonly { label: string; value: string }[];

  /** What the records say is on the shelf, or `undefined` until an item is chosen. */
  level: StockLevelResource | undefined;

  disabled: boolean;

  /** `null` on the last line, which has nothing to be removed to. */
  onRemove: (() => void) | null;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
          Line {index + 1}
        </p>

        {onRemove !== null && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onRemove}
            // Named for what it removes, so a reader moving through several
            // lines hears which one rather than "Remove" three times.
            aria-label={`Remove line ${String(index + 1)}`}
          >
            <IconTrash data-icon="inline-start" />
            Remove
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_10rem]">
        <FormField
          control={control}
          // The index goes into the path as a number rather than through
          // `String()`: stringifying it widens the name to `lines.${string}`,
          // which react-hook-form's `FieldPath` does not accept — and widening
          // the name widens `field.value` to a union of every value in the form,
          // so the control stops being typed as the string it holds.
          name={`lines.${index}.catalogueItemId`}
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Item
              </FormLabel>
              <Select
                value={field.value === '' ? null : field.value}
                // Every stocked item, not just this line's options: the trigger
                // resolves its label from here, and a line's own choice is taken
                // out of the options the moment it is made.
                items={allItems}
                onValueChange={(next) => {
                  field.onChange(typeof next === 'string' ? next : '');
                }}
                disabled={disabled}
              >
                <FormControl
                  render={
                    <SelectTrigger
                      aria-label={`Item on line ${String(index + 1)}`}
                      className={`${fieldSurface} h-11 w-full`}
                    >
                      <SelectValue placeholder="Choose an item" />
                    </SelectTrigger>
                  }
                />
                <SelectContent>
                  <SelectGroup>
                    {options.map((item) => (
                      <SelectItem key={item.catalogueItemId} value={item.catalogueItemId}>
                        {itemLabel(item)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormDescription>
                {level === undefined ? (
                  'Each item once per note.'
                ) : (
                  <>
                    The records say{' '}
                    {/* As it stands. A negative level means the records owe the
                        shelf and is never clamped or hidden (CL-501); a
                        delivery nobody entered is the usual reason for one, so
                        this is exactly the screen where it is worth seeing. */}
                    <span className="font-mono numeric">{formatLevel(level.level)}</span> on the
                    shelf now.
                  </>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lines.${index}.quantity`}
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                How many
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // `inputMode` rather than `type="number"`, as every other
                    // figure in this app: a number input hands the browser its
                    // own opinion — a spinner, a scroll wheel that changes the
                    // quantity under a pointer, and a value that reads back as
                    // `NaN` when it is empty.
                    inputMode="numeric"
                    className={`${fieldSurface} h-11 w-full font-mono numeric`}
                    placeholder="48"
                    autoComplete="off"
                    required
                    disabled={disabled}
                    {...field}
                  />
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
